/**
 * PDP variant → stacked-gallery hero sync (2026-08-16).
 *
 * Bug: with desktop_gallery_layout "stacked", pdp-gallery-plus.css hides the Splide
 * carousel at ≥1024px and shows the server-rendered <ul class="media__stack"> — which
 * nothing updated on variant change. VariantSelects.updateMedia() was faithfully
 * driving an invisible carousel.
 *
 * This script listens for the theme's variant-change contract (the bubbling `change`
 * that updateVariantInput() dispatches on input[name="id"] AFTER updateMedia/updateURL)
 * and makes the selected variant's featured media the stack hero.
 *
 * Rules learned from pdp-gallery-plus.js (do not violate):
 *  - NEVER re-call initStackedTiles() — it would double-bind tile click listeners.
 *    MOVE existing <li> nodes instead: listeners travel with them and their bind-time
 *    slide closures stay valid because data-media-id never changes.
 *  - The lightbox rebuilds its data source at open() and re-resolves visible tiles
 *    per media-id, so reorders and appended tiles are picked up automatically.
 *  - Hero = .media__stack-tile--hero class AND first position (grid auto-placement
 *    + the orphan rule assume hero-first) — both must move together.
 *  - The stack is capped (desktop_gallery_max_images) while the carousel holds every
 *    medium — a variant image beyond the cap has no tile. Evicted tiles are kept in
 *    a detached-node map so their listeners survive a later restore.
 */
(function () {
  'use strict';

  var sections = {}; // sectionId -> { capCount, evicted: Map, variants: Array|null, stackEl }

  function initHost(host) {
    var stack = host.querySelector('.media__stack');
    if (!stack || !host.id || host.id.indexOf('MediaSlideshow-') !== 0) return;
    var sectionId = host.id.slice('MediaSlideshow-'.length);
    sections[sectionId] = { capCount: stack.children.length, evicted: new Map(), variants: null, stackEl: stack };
  }
  document.querySelectorAll('media-slideshow').forEach(initHost);
  if (!Object.keys(sections).length) return;

  function variantsFor(sectionId) {
    var state = sections[sectionId];
    if (state.variants) return state.variants;
    var picker = document.querySelector('variant-radios[data-section="' + sectionId + '"], variant-selects[data-section="' + sectionId + '"]');
    var json = picker && picker.querySelector('script[type="application/json"]');
    try { state.variants = json ? JSON.parse(json.textContent) : []; } catch (e) { state.variants = []; }
    return state.variants;
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.name !== 'id' || !input.form) return;
    var formId = input.form.getAttribute('id') || '';
    if (formId.indexOf('product-form-') !== 0) return;
    var sectionId = formId.slice('product-form-'.length);
    var state = sections[sectionId];
    if (!state) return;

    var host = document.getElementById('MediaSlideshow-' + sectionId);
    var stack = host && host.querySelector('.media__stack');
    if (!stack) return;
    if (stack !== state.stackEl) {
      // theme-editor re-render replaced the stack — old detached nodes are stale
      state.stackEl = stack;
      state.evicted.clear();
      state.capCount = stack.children.length;
    }

    var variant = null;
    var variants = variantsFor(sectionId);
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(input.value)) { variant = variants[i]; break; }
    }
    if (!variant || !variant.featured_media) return; // mirror updateMedia(): keep current media

    var targetId = sectionId + '-' + variant.featured_media.id;
    var hero = stack.querySelector('.media__stack-tile--hero') || stack.firstElementChild;
    if (!hero) return;
    if (hero.getAttribute('data-media-id') === targetId) return; // idempotent: re-selects, double dispatches

    var esc = window.CSS && CSS.escape ? CSS.escape(targetId) : targetId.replace(/"/g, '\\"');
    var tile = stack.querySelector('.media__stack-tile[data-media-id="' + esc + '"]');

    if (!tile && state.evicted.has(targetId)) {
      tile = state.evicted.get(targetId);
      state.evicted.delete(targetId);
      stack.appendChild(tile); // re-attach (position fixed below); listeners were preserved
    }

    if (!tile) {
      /* beyond-cap media: build a tile from the carousel slide (complete source of truth) */
      var slide = host.querySelector('.media__main .splide__slide[data-media-id="' + esc + '"]');
      if (!slide) return; // [hide]-suppressed media: no slide, no-op (matches setActiveMedia)
      if (slide.getAttribute('data-media-type') && slide.getAttribute('data-media-type') !== 'image') return; // video/model: cloning duplicates element ids
      var img = slide.querySelector('img');
      if (!img) return;
      var clone = img.cloneNode(true);
      clone.setAttribute('loading', 'eager');
      tile = document.createElement('li');
      tile.className = 'media__stack-tile';
      tile.setAttribute('data-media-type', 'image');
      tile.setAttribute('data-media-id', targetId);
      tile.appendChild(clone);
      var gp = host.querySelector('pdp-gallery-plus');
      if (gp && gp.getAttribute('data-lightbox') === 'true' && typeof gp.open === 'function') {
        clone.style.cursor = 'zoom-in';
        clone.setAttribute('tabindex', '0');
        clone.setAttribute('role', 'button');
        var openLightbox = function () { try { gp.open(slide); } catch (err) { /* lightbox unavailable */ } };
        clone.addEventListener('click', openLightbox);
        clone.addEventListener('keydown', function (ke) {
          if (ke.key === 'Enter' || ke.key === ' ') { ke.preventDefault(); openLightbox(); }
        });
      }
      stack.appendChild(tile);
    }

    /* promote: transfer the hero class AND move to front (grid placement is DOM-order) */
    var moreBtn = tile.querySelector('.media__stack-more');
    hero.classList.remove('media__stack-tile--hero');
    tile.classList.add('media__stack-tile--hero');
    stack.insertBefore(tile, stack.firstElementChild);
    if (moreBtn && stack.lastElementChild && stack.lastElementChild !== tile) {
      stack.lastElementChild.appendChild(moreBtn); // the +N overlay never sits on the hero
    }

    /* re-enforce the cap; evicted tiles keep their listeners for a later restore */
    while (stack.children.length > state.capCount && stack.lastElementChild !== tile) {
      var last = stack.lastElementChild;
      var lastMore = last.querySelector('.media__stack-more');
      if (lastMore && last.previousElementSibling) last.previousElementSibling.appendChild(lastMore);
      state.evicted.set(last.getAttribute('data-media-id'), last);
      last.remove();
    }
  });
})();
